/**
 * ContractorImportModal — F11 T11.9b FE.
 *
 * Modal importu kontrahentow z XLSX. 3 stany:
 *  1. 'pick' — wybor pliku + link do template + button „Sprawdz plik"
 *  2. 'preview' — wynik dryRun (rowCount + wouldInsert + errors[]) + button
 *     „Importuj naprawde" (disabled gdy errors)
 *  3. 'done' — wynik finalnego importu (imported count) + button „Zamknij"
 *
 * Backend kontrakt:
 *   POST /api/contractors/import?dryRun=true|false (multipart, pole `file`)
 *   - 200 dryRun → { rowCount, wouldInsert, errors: string[] }
 *   - 200 final  → { imported: number, errors: [] }
 *   - 422 final z bledami → { rowCount, wouldInsert: 0, errors: string[] }
 *   - 400 MISSING_FILE / MISSING_HEADER / INVALID_XLSX itp.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Download, FileSpreadsheet, Upload, X } from 'lucide-react';

import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface DryRunResponse {
  rowCount: number;
  wouldInsert: number;
  errors: string[];
}

interface ImportResponse {
  imported: number;
  errors: string[];
}

type Step = 'pick' | 'preview' | 'done';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ContractorImportModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  function reset() {
    setStep('pick');
    setFile(null);
    setDryRun(null);
    setImportResult(null);
    setIsDragOver(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const dryRunMutation = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post<DryRunResponse>('/contractors/import?dryRun=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setDryRun(data);
      setStep('preview');
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data as { error?: string })?.error
        : undefined;
      toast.show({
        variant: 'error',
        title: t(`tenant.contractors.import.error.${code ?? 'GENERIC'}`, {
          defaultValue: t('tenant.contractors.import.error.GENERIC'),
        }),
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post<ImportResponse>('/contractors/import?dryRun=false', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setImportResult(data);
      setStep('done');
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.import.toast.success', { count: data.imported }),
      });
    },
    onError: (err) => {
      // 422 z błędami waliduacyjnymi -> wracamy do preview z listą błędów
      if (isAxiosError(err) && err.response?.status === 422) {
        const data = err.response.data as DryRunResponse;
        setDryRun(data);
        setStep('preview');
        return;
      }
      const code = isAxiosError(err)
        ? (err.response?.data as { error?: string })?.error
        : undefined;
      toast.show({
        variant: 'error',
        title: t(`tenant.contractors.import.error.${code ?? 'GENERIC'}`, {
          defaultValue: t('tenant.contractors.import.error.GENERIC'),
        }),
      });
    },
  });

  function pickFile(f: File | null) {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.import.error.NOT_XLSX'),
      });
      return;
    }
    setFile(f);
    setDryRun(null);
  }

  function handleDrop(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setIsDragOver(false);
    const f = ev.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="lg"
      title={t('tenant.contractors.import.title')}
      description={
        step === 'pick'
          ? t('tenant.contractors.import.description.pick')
          : step === 'preview'
            ? t('tenant.contractors.import.description.preview')
            : t('tenant.contractors.import.description.done')
      }
      footer={renderFooter()}
    >
      {step === 'pick' && (
        <div className="space-y-4" data-testid="contractor-import-step-pick">
          <a
            href="/static/contractors-import-template.xlsx"
            download
            className="inline-flex items-center gap-2 text-sm text-accent-700 hover:underline dark:text-accent-300"
            data-testid="contractor-import-template-link"
          >
            <Download size={16} aria-hidden="true" />
            {t('tenant.contractors.import.downloadTemplate')}
          </a>

          <div
            onDragOver={(ev) => {
              ev.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            className={[
              'rounded-lg border-2 border-dashed p-8 text-center',
              isDragOver
                ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                : 'border-border-secondary',
            ].join(' ')}
            data-testid="contractor-import-dropzone"
          >
            <FileSpreadsheet size={36} className="mx-auto text-text-tertiary" aria-hidden="true" />
            <p className="mt-3 text-sm text-text-primary">
              {file ? file.name : t('tenant.contractors.import.dropHint')}
            </p>
            <label className="mt-3 inline-block cursor-pointer text-sm text-accent-700 hover:underline dark:text-accent-300">
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(ev) => pickFile(ev.target.files?.[0] ?? null)}
                data-testid="contractor-import-file-input"
              />
              {t('tenant.contractors.import.browse')}
            </label>
          </div>
        </div>
      )}

      {step === 'preview' && dryRun && (
        <div className="space-y-3" data-testid="contractor-import-step-preview">
          <div className="rounded border border-border-secondary p-3 text-sm">
            <p>
              <strong>{t('tenant.contractors.import.preview.rowCount')}:</strong>{' '}
              <span data-testid="contractor-import-rowcount">{dryRun.rowCount}</span>
            </p>
            <p>
              <strong>{t('tenant.contractors.import.preview.wouldInsert')}:</strong>{' '}
              <span
                className={dryRun.errors.length > 0 ? 'text-red-600' : 'text-green-700'}
                data-testid="contractor-import-wouldinsert"
              >
                {dryRun.wouldInsert}
              </span>
            </p>
            <p>
              <strong>{t('tenant.contractors.import.preview.errorCount')}:</strong>{' '}
              <span
                className={dryRun.errors.length > 0 ? 'text-red-600' : ''}
                data-testid="contractor-import-errorcount"
              >
                {dryRun.errors.length}
              </span>
            </p>
          </div>

          {dryRun.errors.length > 0 && (
            <div
              className="max-h-64 overflow-y-auto rounded border border-red-200 bg-red-50 p-3 text-xs dark:border-red-800 dark:bg-red-900/20"
              data-testid="contractor-import-errors-list"
            >
              <ul className="space-y-1">
                {dryRun.errors.map((e, i) => (
                  <li key={i} className="text-red-800 dark:text-red-300">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {step === 'done' && importResult && (
        <div className="space-y-3" data-testid="contractor-import-step-done">
          <p className="rounded border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
            {t('tenant.contractors.import.done.summary', { count: importResult.imported })}
          </p>
        </div>
      )}
    </Modal>
  );

  function renderFooter() {
    if (step === 'pick') {
      return (
        <>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-border-secondary px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary"
            data-testid="contractor-import-cancel-btn"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!file || dryRunMutation.isPending}
            onClick={() => file && dryRunMutation.mutate(file)}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
            data-testid="contractor-import-checkfile-btn"
          >
            <Upload size={14} aria-hidden="true" />
            {dryRunMutation.isPending
              ? t('tenant.contractors.import.checking')
              : t('tenant.contractors.import.checkFile')}
          </button>
        </>
      );
    }
    if (step === 'preview') {
      return (
        <>
          <button
            type="button"
            onClick={() => setStep('pick')}
            className="rounded-lg border border-border-secondary px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary"
            data-testid="contractor-import-back-btn"
          >
            <X size={14} className="inline mr-1" aria-hidden="true" />
            {t('tenant.contractors.import.pickAnother')}
          </button>
          <button
            type="button"
            disabled={
              !file ||
              importMutation.isPending ||
              (dryRun?.errors.length ?? 0) > 0 ||
              (dryRun?.wouldInsert ?? 0) === 0
            }
            onClick={() => file && importMutation.mutate(file)}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
            data-testid="contractor-import-confirm-btn"
          >
            {importMutation.isPending
              ? t('tenant.contractors.import.importing')
              : t('tenant.contractors.import.confirm')}
          </button>
        </>
      );
    }
    // done
    return (
      <button
        type="button"
        onClick={handleClose}
        className="rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700"
        data-testid="contractor-import-close-btn"
      >
        {t('common.close')}
      </button>
    );
  }
}
